import { Box } from '@mui/material';
import { UrlParams } from '@provision/portal-routes';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isNullish } from 'remeda';
import { usePartnersDialogOptions } from 'src/components/Navbar/use-partners-dialog-options';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { useCurrentUserProfile } from 'src/graphql';
import {
  SupportPolicyWithNullableDates,
  useCreateSupportPolicy,
  useDeleteSupportPolicy,
  useUpdateSupportPolicy,
} from 'src/graphql/entities/support-policy';
import { serializeSupportPolicyConditions } from 'src/graphql/entities/support-policy/support-policy.conditions';
import { NormalizedSupportPolicy } from 'src/graphql/entities/support-policy/support-policy.utils';
import { useSelectedConversation as useConversationDetails } from 'src/pages/Conversations';
import { useGlobalStore } from 'src/store';
import { Modals, useShowModals } from 'src/store/modals';
import { useSelectedConversation as useSelectedConversationStore } from 'src/store/selected-conversation';
import { useRouteParams } from 'src/utils/routing';
import { SupportPolicyCategoryChips } from './category-chips';
import { EscalationEvaluationContext } from './evaluation-context';
import {
  getFocusTargetCategory,
  getSelectedCategoriesAfterConversationClose,
} from './policies-tab.utils';
import { POLICY_CATEGORIES, POLICY_CATEGORY_ESCALATION } from './policy-constants';
import { SupportPolicyFormValues } from './policy-form';
import { groupPoliciesByCategory, resolvePolicyCategory } from './policy-groups';
import { SupportPolicyLegend } from './policy-legend';
import { SupportPolicyList } from './policy-list';
import { SupportPolicyMenu } from './policy-menu';
import { sortPoliciesForDisplay } from './policy-order';
import { PolicyFilterMode, SupportPolicyTopControls } from './top-controls';
import { useApplicablePolicies } from './use-applicable-policies';
import { useEnsureEscalationPolicies } from './use-ensure-escalation-policies';
import { useNewEscalationPoliciesFlag } from './use-escalation-flag';
import { usePolicyCategoryOpen } from './use-policy-category-open';

const categories = ['All', ...POLICY_CATEGORIES] as const;

const filterPoliciesByCategories = (
  policies: NormalizedSupportPolicy[],
  selected: string[],
): NormalizedSupportPolicy[] => {
  if (!selected.length || selected.includes('All')) {
    return policies;
  }

  const allowed = new Set(selected);
  return policies.filter((policy) => allowed.has(resolvePolicyCategory(policy.category)));
};

const buildCategoryCounts = (policies: NormalizedSupportPolicy[]): Record<string, number> => {
  const counts: Record<string, number> = { All: policies.length };

  categories.forEach((category) => {
    if (category !== 'All') {
      counts[category] = 0;
    }
  });

  policies.forEach((policy) => {
    counts[resolvePolicyCategory(policy.category)] += 1;
  });

  return counts;
};

const isEscalationPolicyCategory = (category?: string | null): boolean =>
  category === POLICY_CATEGORY_ESCALATION;

interface SupportPoliciesTabProps {
  fallbackPartnerId: number;
  focusPolicyId?: string | null;
  onFocusPolicyHandled?: () => void;
}

export const SupportPoliciesTab = ({
  fallbackPartnerId,
  focusPolicyId,
  onFocusPolicyHandled,
}: SupportPoliciesTabProps) => {
  const { user } = useCurrentUserProfile();
  const currentPartnerId = useGlobalStore.use.selectedPartnerId();
  const isMultiTenancyMode = useGlobalStore.use.isMultiTenancyMode();
  const { partnersOptions } = usePartnersDialogOptions({ currentPartner: true });

  const scopeOptions = useMemo(
    () =>
      partnersOptions.map((option) => ({
        id: option.id ?? null,
        label: option.label === 'Multi-Tenancy Mode' ? 'Global Policies' : option.label,
      })),
    [partnersOptions],
  );

  // a pick is remembered against the record in view, so opening a different
  // conversation or site drops it instead of pinning the old partner
  const { recordId: entityContextKey } = useRouteParams(UrlParams.recordId);
  const [scopePick, setScopePick] = useState<{
    contextKey?: string;
    partnerId: number | null;
  } | null>(null);
  const onScopeChange = useCallback(
    (picked: number | null) =>
      setScopePick({ contextKey: entityContextKey, partnerId: picked }),
    [entityContextKey],
  );

  const canChangeScope = isMultiTenancyMode && isNullish(currentPartnerId);
  // must not snapshot the live partner selection: this pane hangs off the navbar
  // and outlives partner switches
  const scopePartnerId =
    canChangeScope && scopePick && scopePick.contextKey === entityContextKey
      ? scopePick.partnerId
      : undefined;

  const [selectedCategories, setSelectedCategories] = useState<string[]>(['All']);
  const [expanded, setExpanded] = useState<string[]>([]);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuPolicyId, setMenuPolicyId] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [policyFilterMode, setPolicyFilterMode] = useState<PolicyFilterMode>('applicable');
  const [showLegend, setShowLegend] = useState(false);

  const { deleteSupportPolicy } = useDeleteSupportPolicy();
  const { updateSupportPolicy, updateSupportPolicyWithGroups } = useUpdateSupportPolicy();
  const { createSupportPolicy } = useCreateSupportPolicy();

  const [showSupportPolicyModal] = useShowModals(Modals.SupportPolicyModal);
  const { conversationId: selectedConversationId } = useSelectedConversationStore();
  const { selectedConversation } = useConversationDetails({
    conversationId: selectedConversationId ?? '',
  });

  const {
    supportPolicies,
    accessiblePolicies,
    applicablePolicies,
    policyEvaluationContext,
    partnerId,
    escalationEval,
    canAccessEscalationPolicies,
    canManageEscalationPolicies,
    canManagePolicies,
    isLoading,
  } = useApplicablePolicies({ fallbackPartnerId, scopePartnerId, showArchived });

  const canSeeEscalationRuntime = useNewEscalationPoliciesFlag(partnerId);

  const scopeLabel = useMemo(
    () => scopeOptions.find((option) => option.id === partnerId)?.label ?? 'Global Policies',
    [scopeOptions, partnerId],
  );

  // with no pick the menu ticks whatever the open entity resolved to
  const scopeSelection = useMemo(() => {
    if (scopePartnerId !== undefined) {
      return scopePartnerId;
    }
    const contextPartnerId = partnerId ?? PLATFORM_PARTNER_ID;
    return contextPartnerId === PLATFORM_PARTNER_ID ? null : contextPartnerId;
  }, [scopePartnerId, partnerId]);

  const availableCategories = useMemo(
    () =>
      canAccessEscalationPolicies
        ? categories
        : categories.filter((category) => category !== POLICY_CATEGORY_ESCALATION),
    [canAccessEscalationPolicies],
  );

  useEffect(() => {
    if (canAccessEscalationPolicies) {
      return;
    }

    setSelectedCategories((prev) =>
      prev.includes(POLICY_CATEGORY_ESCALATION) ? ['All'] : prev,
    );
  }, [canAccessEscalationPolicies]);

  const hadConversationRef = useRef(false);

  useEffect(() => {
    const hasConversation = Boolean(selectedConversationId && selectedConversation);
    const hadConversation = hadConversationRef.current;

    if (
      hadConversation &&
      !hasConversation &&
      selectedCategories.includes(POLICY_CATEGORY_ESCALATION)
    ) {
      setSelectedCategories((prev) => getSelectedCategoriesAfterConversationClose(prev));
    }

    hadConversationRef.current = hasConversation;
  }, [selectedCategories, selectedConversation, selectedConversationId]);

  const mapFormValuesToPayload = useCallback(
    (values: SupportPolicyFormValues): Partial<SupportPolicyWithNullableDates> => ({
      title: values.title,
      description: values.description,
      type: values.type,
      category: values.category,
      validUntil: values.validUntil ? values.validUntil.toISOString() : null,
      pinnedAt: values.pinnedAt ? new Date().toISOString() : null,
      addressIds: JSON.stringify(values.addressIds ?? []),
      clientIds: JSON.stringify(values.clientIds ?? []),
      clientToAddressIds: JSON.stringify(values.clientToAddressIds ?? []),
      conditions:
        serializeSupportPolicyConditions(values.conditions, values.enableConditions) ?? '',
      urgencyLevel: values.urgencyLevel ?? null,
      escalationRules: values.escalationRules ?? null,
    }),
    [],
  );

  const handleExpandClick = (id: string) => {
    setExpanded((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleClearCategoryFilters = useCallback(() => {
    setSelectedCategories(['All']);
    setPolicyFilterMode('all');
  }, []);

  const totalPoliciesCount = accessiblePolicies.length;
  const applicablePoliciesCount = applicablePolicies.length;
  const policiesByMode =
    policyFilterMode === 'applicable' ? applicablePolicies : accessiblePolicies;

  const filteredPolicies = useMemo(
    () => filterPoliciesByCategories(policiesByMode, selectedCategories),
    [policiesByMode, selectedCategories],
  );

  // grouping partitions the shared display order; it never re-sorts inside a group
  const policyGroups = useMemo(
    () => groupPoliciesByCategory(sortPoliciesForDisplay(filteredPolicies)),
    [filteredPolicies],
  );

  const categoryOpen = usePolicyCategoryOpen(policyGroups);

  const handleCategoryToggle = useCallback(
    (category: string) => {
      const isSelecting = category !== 'All' && !selectedCategories.includes(category);

      setSelectedCategories((prev) => {
        if (category === 'All') {
          return ['All'];
        }

        const withoutAll = prev.filter((c) => c !== 'All');
        const isSelected = withoutAll.includes(category);
        const nextSelection = isSelected
          ? withoutAll.filter((c) => c !== category)
          : [...withoutAll, category];

        return nextSelection.length === 0 ? ['All'] : nextSelection;
      });

      // filtering to a category must not leave it folded shut
      if (isSelecting) {
        categoryOpen.open(category);
      }
    },
    [selectedCategories, categoryOpen],
  );

  const categoryCountsForAll = useMemo(
    () => buildCategoryCounts(accessiblePolicies),
    [accessiblePolicies],
  );
  const categoryCountsForApplicable = useMemo(
    () => buildCategoryCounts(applicablePolicies),
    [applicablePolicies],
  );
  const categoryCounts =
    policyFilterMode === 'applicable' ? categoryCountsForApplicable : categoryCountsForAll;

  const nonAllSelectedCategories = useMemo(
    () => (selectedCategories.includes('All') ? [] : selectedCategories),
    [selectedCategories],
  );

  const archivedCount = useMemo(
    () => supportPolicies.filter((policy) => policy.archivedAt).length,
    [supportPolicies],
  );

  const handledFocusRef = useRef<string | null>(null);

  useEffect(() => {
    // the policy query is skipped until the scope resolves a partner, and a
    // skipped query reads as loaded — so wait for the target itself, not a flag,
    // or the single shot burns on an empty list and opens the wrong category
    const hasTarget =
      !!focusPolicyId && accessiblePolicies.some((policy) => policy.id === focusPolicyId);

    if (!focusPolicyId || !hasTarget || handledFocusRef.current === focusPolicyId) {
      return;
    }
    handledFocusRef.current = focusPolicyId;

    setExpanded((prev) => (prev.includes(focusPolicyId) ? prev : [...prev, focusPolicyId]));

    // the group has to open in the same commit, or the card scrolls to nothing
    categoryOpen.open(getFocusTargetCategory(accessiblePolicies, focusPolicyId));

    // a deep link must not land on a card the filters are hiding
    if (!filteredPolicies.some((policy) => policy.id === focusPolicyId)) {
      setSelectedCategories(['All']);
      setPolicyFilterMode('applicable');
    }
  }, [focusPolicyId, accessiblePolicies, filteredPolicies, categoryOpen]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, id: string) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuPolicyId(id);
  };

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
    setMenuPolicyId(null);
  }, [setMenuAnchorEl, setMenuPolicyId]);

  const handleEdit = useCallback(
    (values: SupportPolicyFormValues, policy: Partial<NormalizedSupportPolicy>) => {
      if (!policy.id) return;
      if (
        (isEscalationPolicyCategory(values.category) ||
          isEscalationPolicyCategory(policy.category)) &&
        !canManageEscalationPolicies
      ) {
        return;
      }

      const updatePayload = mapFormValuesToPayload(values);
      void updateSupportPolicyWithGroups({
        id: policy.id,
        partial: updatePayload,
        userGroupIds: values.userGroups || [],
        supportPolicy: policy as unknown as SupportPolicyWithNullableDates,
      });
    },
    [canManageEscalationPolicies, mapFormValuesToPayload, updateSupportPolicyWithGroups],
  );

  const handlePolicyUpdate = useCallback(
    (
      policy: NormalizedSupportPolicy,
      updates: { archivedAt?: string | null; pinnedAt?: string | null },
    ) => {
      // @ts-expect-error Generated type mismatch
      void updateSupportPolicy(policy.id, updates, policy);
      handleMenuClose();
    },
    [handleMenuClose, updateSupportPolicy],
  );

  const handlePin = (policy: NormalizedSupportPolicy) =>
    handlePolicyUpdate(policy, { pinnedAt: new Date().toISOString() });

  const handleUnpin = (policy: NormalizedSupportPolicy) =>
    handlePolicyUpdate(policy, { pinnedAt: null });

  const handleArchive = (policy: NormalizedSupportPolicy) =>
    handlePolicyUpdate(policy, { archivedAt: new Date().toISOString(), pinnedAt: null });

  const handleUnarchive = (policy: NormalizedSupportPolicy) =>
    handlePolicyUpdate(policy, { archivedAt: null });

  const handleDelete = useCallback(
    (id: string | null) => {
      if (!id) return;
      void deleteSupportPolicy(id);
      handleMenuClose();
    },
    [handleMenuClose, deleteSupportPolicy],
  );

  const handleCreate = useCallback(
    (values: SupportPolicyFormValues) => {
      if (!user?.id) return;
      if (isEscalationPolicyCategory(values.category) && !canManageEscalationPolicies) {
        return;
      }

      const resolvedPartnerId = isNullish(partnerId) ? PLATFORM_PARTNER_ID : partnerId;

      void createSupportPolicy({
        ...values,
        partnerId: resolvedPartnerId,
        createdBy: user.id,
      });
    },
    [canManageEscalationPolicies, createSupportPolicy, partnerId, user],
  );

  useEnsureEscalationPolicies({
    partnerId,
    policies: supportPolicies,
    isLoading,
    onCreate: handleCreate,
    enabled: canManageEscalationPolicies,
  });

  const handleAddPolicy = useCallback(() => {
    showSupportPolicyModal({
      policy: null,
      onSave: handleCreate,
    });
  }, [handleCreate, showSupportPolicyModal]);

  const handleEditPolicy = useCallback(
    (policy: NormalizedSupportPolicy) => {
      showSupportPolicyModal({
        policy,
        onSave: (values) => handleEdit(values, policy),
      });
    },
    [handleEdit, showSupportPolicyModal],
  );

  const handlePolicyFilterModeChange = useCallback((mode: PolicyFilterMode) => {
    setPolicyFilterMode(mode);
  }, []);

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      data-testid="support-policies-tab"
    >
      {/* Top Controls */}
      <SupportPolicyTopControls
        hasAccessToManageSupportPolicies={canManagePolicies}
        scopeLabel={scopeLabel}
        scopeOptions={scopeOptions}
        scopeValue={scopeSelection}
        onScopeChange={onScopeChange}
        canChangeScope={canChangeScope}
        showArchived={showArchived}
        setShowArchived={setShowArchived}
        archivedCount={archivedCount}
        onAddPolicy={handleAddPolicy}
        policyFilterMode={policyFilterMode}
        onPolicyFilterModeChange={handlePolicyFilterModeChange}
        totalPoliciesCount={totalPoliciesCount}
        applicablePoliciesCount={applicablePoliciesCount}
        displayedPoliciesCount={filteredPolicies.length}
        categories={availableCategories}
        selectedCategories={selectedCategories}
        onCategoryToggle={handleCategoryToggle}
        categoryCounts={categoryCounts}
        showLegend={showLegend}
        onToggleLegend={() => setShowLegend((prev) => !prev)}
      />

      {showLegend ? <SupportPolicyLegend /> : null}

      <SupportPolicyCategoryChips
        selectedCategories={nonAllSelectedCategories}
        onRemoveCategory={handleCategoryToggle}
        onClearAll={handleClearCategoryFilters}
      />

      {canSeeEscalationRuntime &&
      canManageEscalationPolicies &&
      selectedCategories.includes(POLICY_CATEGORY_ESCALATION) ? (
        <EscalationEvaluationContext
          serviceLevel={escalationEval.serviceLevel}
          onServiceLevelChange={escalationEval.setServiceLevel}
          currentTime={escalationEval.currentTime}
          onCurrentTimeChange={escalationEval.setCurrentTime}
          isHoliday={escalationEval.isHoliday}
          onHolidayChange={escalationEval.setIsHoliday}
        />
      ) : null}

      {/* Scrollable Policy List */}
      <SupportPolicyList
        // the resolved partner (scope > site > conversation), so a platform policy
        // is only manageable while the viewer is actually scoped to the platform;
        // the fallback covers the tick where a site's address has not loaded yet
        partnerId={partnerId ?? fallbackPartnerId}
        focusPolicyId={focusPolicyId}
        onFocusPolicyHandled={onFocusPolicyHandled}
        isLoading={isLoading}
        policyGroups={policyGroups}
        isCategoryOpen={categoryOpen.isOpen}
        onToggleCategory={categoryOpen.toggle}
        expanded={expanded}
        handleExpandClick={handleExpandClick}
        hasAccessToManageSupportPolicies={canManagePolicies}
        handleMenuOpen={handleMenuOpen}
        policyEvaluationContext={policyEvaluationContext}
        evaluationServiceLevel={escalationEval.serviceLevel}
        isPreLits={escalationEval.isPreLits}
        getEscalationCutoffHoursForUrgency={escalationEval.getEscalationCutoffHoursForUrgency}
      />

      {/* Menu for policy actions (only if user has manage access) */}
      <SupportPolicyMenu
        hasAccessToManageSupportPolicies={canManagePolicies}
        hasAccessToManageEscalationPolicies={canManageEscalationPolicies}
        menuAnchorEl={menuAnchorEl}
        setMenuAnchorEl={setMenuAnchorEl}
        menuPolicyId={menuPolicyId}
        setMenuPolicyId={setMenuPolicyId}
        supportPolicies={supportPolicies}
        handleUnpin={handleUnpin}
        handlePin={handlePin}
        handleArchive={handleArchive}
        handleUnarchive={handleUnarchive}
        handleDelete={handleDelete}
        onEditPolicy={handleEditPolicy}
      />
    </Box>
  );
};
